/**
 * useWebPush (BL-205) — React binding over the Web Push subscription helpers.
 *
 * Gives the notification-settings toggle a `subscribe()`/`unsubscribe()` pair
 * that manages the browser subscription + server registration, using the SDK
 * client from context. Pass the app's VAPID public key (VITE_VAPID_PUBLIC_KEY);
 * an empty key makes everything a graceful no-op (feature dark without a key).
 */

import { useCallback } from 'react';
import { useSdkContext } from '../providers/SdkProvider';
import {
  isWebPushSupported,
  subscribeWebPush,
  unsubscribeWebPush,
  type SubscribeWebPushResult,
} from '../web-push';

export interface UseWebPushResult {
  /** Whether this browser can do Web Push at all. */
  supported: boolean;
  /** Whether a VAPID key was provided (feature is configured). */
  configured: boolean;
  /** Prompt permission + subscribe + register. Call from a user gesture. */
  subscribe: () => Promise<SubscribeWebPushResult>;
  /** Unsubscribe this browser + unregister server-side. */
  unsubscribe: () => Promise<void>;
}

export function useWebPush(vapidPublicKey: string | undefined): UseWebPushResult {
  const { client } = useSdkContext();
  const key = vapidPublicKey ?? '';

  const subscribe = useCallback(
    () =>
      key
        ? subscribeWebPush({ client, vapidPublicKey: key })
        : Promise.resolve({ ok: false, reason: 'no-vapid-key' } as SubscribeWebPushResult),
    [client, key]
  );

  const unsubscribe = useCallback(() => unsubscribeWebPush(client), [client]);

  return {
    supported: isWebPushSupported(),
    configured: !!key,
    subscribe,
    unsubscribe,
  };
}
